import type { Attachment } from '@discordo/shared';
import { useCallback, useState, type ReactElement } from 'react';

import { ApiError } from '../lib/api';

/**
 * Files in the interface: how one is shown inside a message, and what is on
 * screen while one is being sent.
 *
 * Nothing here knows the bucket exists. An attachment's `url` is a path on this
 * API, which proves the caller belongs to the server that owns the file before
 * redirecting to a signed URL that expires in minutes — so a thumbnail is a
 * request like any other, and a link nobody may follow answers a refusal
 * instead of the bytes.
 */

const GENERIC_FAILURE = 'Não foi possível enviar o arquivo. Tente de novo.';

// ---------------------------------------------------------------------------
// Wording
// ---------------------------------------------------------------------------

const KILOBYTE = 1024;
const UNITS = ['B', 'kB', 'MB', 'GB'] as const;

const sizeFormat = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

/** Size in the largest unit that leaves a number worth reading. */
export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;

  while (value >= KILOBYTE && unit < UNITS.length - 1) {
    value /= KILOBYTE;
    unit += 1;
  }

  return `${sizeFormat.format(value)} ${UNITS[unit]}`;
}

/**
 * The stored type — read from the bytes by the API, never from what the browser
 * declared — worded for whoever is reading the message.
 */
export function describeAttachmentType(contentType: string): string {
  const [kind, subtype = ''] = contentType.split('/');
  const label = subtype.toUpperCase();

  switch (kind) {
    case 'image':
      return `Imagem ${label}`;
    case 'video':
      return `Vídeo ${label}`;
    case 'audio':
      return `Áudio ${label}`;
    case 'text':
      return 'Texto';
    default:
      return contentType === 'application/pdf' ? 'PDF' : contentType;
  }
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

export interface AttachmentViewProps {
  attachment: Attachment;
}

/**
 * One file inside a message.
 *
 * An image is its own preview — that is what makes a picture worth sending —
 * and clicking it opens the full size. Everything else becomes a card naming
 * what it is, because a filename alone does not say whether it is worth the
 * download.
 */
export function AttachmentView({ attachment }: AttachmentViewProps): ReactElement {
  if (isImage(attachment.contentType)) {
    return (
      <a
        className="attachment attachment-image"
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Abrir ${attachment.filename}`}
      >
        <img
          className="attachment-thumb"
          src={attachment.url}
          alt={attachment.filename}
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <a className="attachment attachment-card" href={attachment.url} target="_blank" rel="noreferrer">
      <span className="attachment-sigil" aria-hidden="true">
        📎
      </span>
      <span className="attachment-body">
        <span className="attachment-name">{attachment.filename}</span>
        <span className="attachment-meta">
          {describeAttachmentType(attachment.contentType)} · {formatBytes(attachment.sizeBytes)}
        </span>
      </span>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

export interface AttachmentUploadState {
  /** Name of the file on its way up; `null` when nothing is being sent. */
  filename: string | null;
  /** How much of the body has left the browser, from 0 to 1. */
  progress: number;
  error: string | null;
  send: (file: File) => void;
  dismiss: () => void;
}

/**
 * Drives one upload at a time.
 *
 * One at a time on purpose: the composer and the drop zone both feed this, and
 * two files racing would leave a single progress bar telling the truth about
 * neither. A second file asked for while one is in flight is simply ignored —
 * the bar on screen already says why.
 */
export function useAttachmentUpload(
  send: (file: File, onProgress?: (fraction: number) => void) => Promise<void>,
): AttachmentUploadState {
  const [filename, setFilename] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(
    (file: File) => {
      if (filename !== null) {
        return;
      }

      setFilename(file.name);
      setProgress(0);
      setError(null);

      send(file, setProgress)
        .catch((cause: unknown) => {
          // The API writes its refusals for the reader — too large, wrong type,
          // server out of room — so the message it sent is the message shown.
          setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
        })
        .finally(() => {
          setFilename(null);
          setProgress(0);
        });
    },
    [filename, send],
  );

  const dismiss = useCallback(() => {
    setError(null);
  }, []);

  return { filename, progress, error, send: start, dismiss };
}

export interface AttachmentUploadProps {
  state: AttachmentUploadState;
}

/** The strip above the composer: the bar while it climbs, the refusal after. */
export function AttachmentUpload({ state }: AttachmentUploadProps): ReactElement | null {
  if (state.error !== null) {
    return (
      <p className="attachment-upload-error" role="alert">
        {state.error}
        <button
          type="button"
          className="attachment-upload-dismiss"
          aria-label="Fechar o aviso"
          onClick={state.dismiss}
        >
          ×
        </button>
      </p>
    );
  }

  if (state.filename === null) {
    return null;
  }

  const percent = Math.round(state.progress * 100);

  return (
    <div className="attachment-upload">
      <span className="attachment-upload-name">{state.filename}</span>
      <div
        className="attachment-upload-track"
        role="progressbar"
        aria-label={`Enviando ${state.filename}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div className="attachment-upload-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
